"use client";

import React, { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useGlobalModalStack, MODAL_IDS } from "@wtc/ui";
import { useMediaQuery } from "@/lib/hooks/ui/use-media-query";

// Generic components
import { UniversalEntityPage } from "@/components/shared/UniversalEntityPage";
import { useDealHooks } from "@/features/deals/hooks/use-deals-hooks";

// Deal-specific components
import { getEntityTableConfig } from "@/lib/entity-registry";
import type { Deal } from "@/lib/types/database/deals";

const ENHANCED_DEALS_CONFIG = getEntityTableConfig<Deal>("deals");

// Types
import type { EntityFormProps } from "@/components/shared/UniversalEntityPage/types";
import type { CompanyEntity } from "@/lib/types/company";

interface CompanyDealsProps {
  company: CompanyEntity;
  className?: string;
}

export const CompanyDeals: React.FC<CompanyDealsProps> = ({ company, className = "" }) => {
  // Call hooks at the top level first
  const dealHooks = useDealHooks();
  const globalModalStack = useGlobalModalStack();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Entity form props for template support
  const entityFormProps: EntityFormProps<Deal> = useMemo(() => ({}), []);

  // Create a custom config that filters by company
  const companyDealConfig = useMemo(
    () => ({
      ...ENHANCED_DEALS_CONFIG,
      // Add company-specific filters
      defaultFilters: {
        company_id: company.id,
      },
      // Override display name for context
      displayName: `Deals for ${company.name}`,
    }),
    [company.id, company.name]
  );

  // Custom header component with stats and create button
  const CompanyDealsHeader = () => {
    return (
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">
            Deals for {company.name}
          </h3>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-xs">
              Company: {company.name}
            </Badge>
          </div>
        </div>

        {/* Create Deal Button */}
        <div className="flex items-center gap-3">
          <Button
            onClick={() => {
              globalModalStack.openModal(MODAL_IDS.deals.create);
            }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="h-4 w-4" />
            Create Deal
          </Button>
        </div>
      </div>
    );
  };

  // Custom filter to show only deals for this company
  const customFilters = useMemo(
    () => ({
      company_id: company.id,
    }),
    [company.id]
  );

  return (
    <div className={className}>
      <CompanyDealsHeader />

      {/* Use the generic UniversalEntityPage with company-specific configuration */}
      <UniversalEntityPage
        entityName="deals"
        config={companyDealConfig}
        hooks={dealHooks}
        filterPreset="all"
        title={`Deals for ${company.name}`}
        description={`Manage deals for ${company.name}`}
        storageKey={`company-${company.id}-deals-column-preferences`}
        // Pass company filter to the hooks (same format as contacts)
        customFilters={customFilters}
        entityFormProps={entityFormProps}
        // Hide the default header since we have a custom one
        showHeader={false}
      />
    </div>
  );
};
